import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { companiesService } from "@shared/services/companiesService";
import { levelOfStructureService } from "@modules/organizationalStructure/levelOfStructure/services/levelOfStructure";
import { structuralGroupService } from "@modules/organizationalStructure/structuralGroups/services/structuralGroups";
import { rejectBlockingFetchError } from "@shared/services/blockingError";

export const fetchCompanies = createAsyncThunk(
    "structuralGroup/fetchCompanies",
    async (_, { rejectWithValue, getState }) => {
        try {
            const companies = await companiesService.getAllCompanies();
            const adminBranchId = getState().auth?.adminBranchId ?? null;
            return { companies, adminBranchId };
        } catch (error) {
            return rejectBlockingFetchError(error, rejectWithValue, 'Failed to load companies');
        }
    }
)

export const fetchLevels = createAsyncThunk(
    "structuralGroup/fetchLevels",
    async (companyId, { rejectWithValue }) => {
        try {
            return await levelOfStructureService.getAllLevels(companyId);
        } catch (error) {
            return rejectBlockingFetchError(error, rejectWithValue, 'Failed to load levels');
        }
    }
)

export const fetchStructuralGroups = createAsyncThunk(
    "structuralGroup/fetchStructuralGroups",
    async ({ levelId, searchParams = {} }, { rejectWithValue }) => {
        try {
            return await structuralGroupService.getStructuralGroupsByLevel(levelId, searchParams);
        } catch (error) {
            return rejectBlockingFetchError(error, rejectWithValue, 'Failed to load structural groups');
        }
    }
)

export const fetchPositions = createAsyncThunk(
    "structuralGroup/fetchPositions",
    async ({ structuralGroupId, searchParams = {} }, { rejectWithValue }) => {
        try {
            return await structuralGroupService.getPositionCountsWithSearch(structuralGroupId, searchParams);
        } catch (error) {
            return rejectBlockingFetchError(error, rejectWithValue, 'Failed to load positions');
        }
    }
)

export const fetchDrillDownData = createAsyncThunk(
    "structuralGroup/fetchDrillDownData",
    async ({ selectedCompany, parentId }, { rejectWithValue }) => {
        try {
            return await levelOfStructureService.getOrgUnitById(selectedCompany, { parentId });
        } catch (error) {
            return rejectBlockingFetchError(error, rejectWithValue, 'Failed to load drill-down data');
        }
    }
)

export const fetchPositionsByJobId = createAsyncThunk(
    "structuralGroup/fetchPositionsByJobId",
    async ({ selectedRecordId, positionId }, { rejectWithValue }) => {
        try {
            const response = await structuralGroupService.getPositionsByJobId(selectedRecordId, positionId);
            return response.map(item => {
                const ocupant = item.ocupationHistory?.[0]?.ocupantId;
                const ocupantName = ocupant?.name && ocupant?.surname
                    ? `${ocupant.name} ${ocupant.surname}`
                    : '';

                return {
                    id: item.id,
                    positionNumber: item.positionNumber,
                    positionName: item.hrOrgUnit?.longDescription || '',
                    ocupantName,
                    jobDescriptionName: item.jobDescriptionName || '',
                    originalData: item
                };
            });
        } catch (error) {
            return rejectBlockingFetchError(error, rejectWithValue, 'Failed to load positions drill-down data');
        }
    }
)
const structuralGroupSlice = createSlice({
    name: "structuralGroup",
    initialState: {
        navigationSource: null,
        companies: [],
        selectedCompany: null,
        companiesLoading: false,
        error: null,
        levels: [],
        levelsLoading: false,
        selectedLevel: null,
        structuralGroups: [],
        structuralGroupsLoading: false,
        positions: [],
        positionsLoading: false,
        searchState: {
            structuralGroups: {
                query: {},
                type: ''
            },
            positions: {
                query: {},
                type: ''
            }
        },
        // Selection state
        selectedRecord: null,
        selectedData: null,
        checkboxSelectedRecord: null,
        positionsCheckboxSelectedRecord: null,
        // Drill-down state
        drillDownData: [],
        isDrillDownMode: false,
        parentId: null,
        breadcrumbPath: [],
        // Positions drill-down state
        positionsDrillDownData: [],
        isPositionsDrillDownMode: false,
        positionsBreadcrumbPath: [],
        // UI state
        modalState: { visible: false, type: null, editData: null },
        positionsColumns: [],
        fullCompanyObject: null
    },
    reducers: {
        setNavigationSource: (state, action) => {
            state.navigationSource = action.payload;
        },
        setCompanies: (state, action) => {
            state.companies = action.payload;
        },
        setSelectedCompany: (state, action) => {
            state.selectedCompany = action.payload;
        },
        setCompaniesLoading: (state, action) => {
            state.companiesLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setLevels: (state, action) => {
            state.levels = action.payload;
        },
        setSelectedLevel: (state, action) => {
            state.selectedLevel = action.payload;
        },
        setLevelsLoading: (state, action) => {
            state.levelsLoading = action.payload;
        },
        setStructuralGroups: (state, action) => {
            state.structuralGroups = action.payload;
        },
        setStructuralGroupsLoading: (state, action) => {
            state.structuralGroupsLoading = action.payload;
        },
        setPositions: (state, action) => {
            state.positions = action.payload;
        },
        setPositionsLoading: (state, action) => {
            state.positionsLoading = action.payload;
        },
        setStructuralGroupsSearchQuery: (state, action) => {
            state.searchState.structuralGroups.query = action.payload;
        },
        setStructuralGroupsSearchType: (state, action) => {
            state.searchState.structuralGroups.type = action.payload;
        },
        setPositionsSearchQuery: (state, action) => {
            state.searchState.positions.query = action.payload;
        },
        setPositionsSearchType: (state, action) => {
            state.searchState.positions.type = action.payload;
        },
        clearAllSearches: (state) => {
            state.searchState.structuralGroups = { query: {}, type: '' };
            state.searchState.positions = { query: {}, type: '' };
        },
        // Selection state reducers
        setSelectedRecord: (state, action) => {
            state.selectedRecord = action.payload;
        },
        setSelectedData: (state, action) => {
            state.selectedData = action.payload;
        },
        setCheckboxSelectedRecord: (state, action) => {
            state.checkboxSelectedRecord = action.payload;
        },
        setPositionsCheckboxSelectedRecord: (state, action) => {
            state.positionsCheckboxSelectedRecord = action.payload;
        },
        // Drill-down state reducers
        setDrillDownData: (state, action) => {
            state.drillDownData = action.payload;
        },
        setIsDrillDownMode: (state, action) => {
            state.isDrillDownMode = action.payload;
        },
        setParentId: (state, action) => {
            state.parentId = action.payload;
        },
        setBreadcrumbPath: (state, action) => {
            state.breadcrumbPath = action.payload;
        },
        addToBreadcrumbPath: (state, action) => {
            state.breadcrumbPath.push(action.payload);
        },
        // Positions drill-down reducers
        setPositionsDrillDownData: (state, action) => {
            state.positionsDrillDownData = action.payload;
        },
        setIsPositionsDrillDownMode: (state, action) => {
            state.isPositionsDrillDownMode = action.payload;
        },
        setPositionsBreadcrumbPath: (state, action) => {
            state.positionsBreadcrumbPath = action.payload;
        },
        addToPositionsBreadcrumbPath: (state, action) => {
            state.positionsBreadcrumbPath.push(action.payload);
        },
        // UI state reducers
        setModalState: (state, action) => {
            state.modalState = action.payload;
        },
        setPositionsColumns: (state, action) => {
            state.positionsColumns = action.payload;
        },
        setFullCompanyObject: (state, action) => {
            state.fullCompanyObject = action.payload;
        },
        // Reset actions
        resetToRoot: (state) => {
            state.isDrillDownMode = false;
            state.drillDownData = [];
            state.selectedData = null;
            state.selectedRecord = null;
            state.checkboxSelectedRecord = null;
            state.positions = [];
            state.parentId = null;
            state.breadcrumbPath = [];
            state.isPositionsDrillDownMode = false;
            state.positionsDrillDownData = [];
            state.positionsCheckboxSelectedRecord = null;
            state.positionsBreadcrumbPath = [];
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCompanies.pending, (state) => {
            state.companiesLoading = true;
        })
            .addCase(fetchCompanies.fulfilled, (state, action) => {
                state.companiesLoading = false;
                const { companies, adminBranchId } = action.payload;
                state.companies = companies || [];

                if (!companies?.length) {
                    return;
                }

                if (adminBranchId != null) {
                    const adminCompany = companies.find(
                        (company) => String(company.id) === String(adminBranchId)
                    );
                    if (adminCompany) {
                        state.selectedCompany = adminCompany.id;
                        return;
                    }
                }

                state.selectedCompany = companies[0].id;
            })
            .addCase(fetchCompanies.rejected, (state, action) => {
                state.companiesLoading = false;
                if (action.payload?.blocked) {
                    state.error = null;
                    state.companies = [];
                    state.selectedCompany = null;
                } else {
                    state.error = action.payload?.message || 'Failed to load companies';
                }
            })
            .addCase(fetchLevels.pending, (state) => {
                state.levelsLoading = true;
            })
            .addCase(fetchLevels.fulfilled, (state, action) => {
                state.levelsLoading = false;
                state.levels = action.payload;
                if (action.payload && action.payload.length > 0) {
                    const levelWithPlanLevel0 = action.payload.find(level => level.planLevel === 0);
                    if (levelWithPlanLevel0) {
                        state.selectedLevel = levelWithPlanLevel0.id;
                    }
                }
            })
            .addCase(fetchLevels.rejected, (state, action) => {
                state.levelsLoading = false;
                if (action.payload?.blocked) {
                    state.error = null;
                    state.levels = [];
                    state.selectedLevel = null;
                } else {
                    state.error = action.payload?.message || 'Failed to load levels';
                }
            })
            .addCase(fetchStructuralGroups.pending, (state) => {
                state.structuralGroupsLoading = true;
            })
            .addCase(fetchStructuralGroups.fulfilled, (state, action) => {
                state.structuralGroupsLoading = false;
                state.structuralGroups = action.payload;
            })
            .addCase(fetchStructuralGroups.rejected, (state, action) => {
                state.structuralGroupsLoading = false;
                state.structuralGroups = [];
                if (action.payload?.blocked) {
                    state.error = null;
                } else {
                    state.error = action.payload?.message || 'Failed to load structural groups';
                }
            })
            .addCase(fetchPositions.pending, (state) => {
                state.positionsLoading = true;
            })
            .addCase(fetchPositions.fulfilled, (state, action) => {
                state.positionsLoading = false;
                state.positions = action.payload;
            })
            .addCase(fetchPositions.rejected, (state, action) => {
                state.positionsLoading = false;
                state.positions = [];
                if (action.payload?.blocked) {
                    state.error = null;
                } else {
                    state.error = action.payload?.message || 'Failed to load positions';
                }
            })
            .addCase(fetchDrillDownData.pending, (state) => {
                state.structuralGroupsLoading = true;
            })
            .addCase(fetchDrillDownData.fulfilled, (state, action) => {
                state.structuralGroupsLoading = false;
                state.drillDownData = action.payload;
            })
            .addCase(fetchDrillDownData.rejected, (state, action) => {
                state.structuralGroupsLoading = false;
                state.drillDownData = [];
                if (action.payload?.blocked) {
                    state.error = null;
                } else {
                    state.error = action.payload?.message || 'Failed to load drill-down data';
                }
            })
            .addCase(fetchPositionsByJobId.pending, (state) => {
                state.positionsLoading = true;
            })
            .addCase(fetchPositionsByJobId.fulfilled, (state, action) => {
                state.positionsLoading = false;
                state.positionsDrillDownData = action.payload;
            })
            .addCase(fetchPositionsByJobId.rejected, (state, action) => {
                state.positionsLoading = false;
                state.positionsDrillDownData = [];
                if (action.payload?.blocked) {
                    state.error = null;
                } else {
                    state.error = action.payload?.message || 'Failed to load positions drill-down data';
                }
            })
    }
})

export const {
    setNavigationSource,
    setCompanies,
    setSelectedCompany,
    setCompaniesLoading,

    setLevels,
    setSelectedLevel,
    setLevelsLoading,

    setStructuralGroups,
    setStructuralGroupsLoading,

    setPositions,
    setPositionsLoading,

    setStructuralGroupsSearchQuery,
    setStructuralGroupsSearchType,
    setPositionsSearchQuery,
    setPositionsSearchType,
    clearAllSearches,

    // Selection state
    setSelectedRecord,
    setSelectedData,
    setCheckboxSelectedRecord,
    setPositionsCheckboxSelectedRecord,

    // Drill-down state
    setDrillDownData,
    setIsDrillDownMode,
    setParentId,
    setBreadcrumbPath,
    addToBreadcrumbPath,

    // Positions drill-down
    setPositionsDrillDownData,
    setIsPositionsDrillDownMode,
    setPositionsBreadcrumbPath,
    addToPositionsBreadcrumbPath,

    // UI state
    setModalState,
    setPositionsColumns,
    setFullCompanyObject,

    // Reset actions
    resetToRoot,

    setError
} = structuralGroupSlice.actions;
export default structuralGroupSlice.reducer;